import { useCallback, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  Loader2,
  Plus,
  RefreshCcw,
  Trash2,
} from 'lucide-react';
import { PillButton } from '@ab-platform/ui/pill-button';
import { SoftBadge } from '@ab-platform/ui/soft-badge';
import { FieldError } from '@ab-platform/ui/field-error';
import { cn } from '@ab-platform/ui/utils';
import type {
  UploadDocumentType,
  UploadMimeType,
} from '@/lib/api';

/**
 * One document slot rendered as a card with a click-or-drop affordance.
 *
 * Visual model (inspired by external compact upload patterns —
 * generic terms only):
 *   IDLE      compact drop zone with `[+] Aggiungi documento` CTA + format hint
 *   UPLOADING file-row with spinner + filename
 *   UPLOADED  file-row with file icon + filename + size + replace/remove actions
 *   ERROR     file-row with alert icon + error message + retry/remove actions
 *
 * The whole card area accepts file drops. Click on the IDLE drop zone or
 * the "Sostituisci"/"Riprova" button opens the native picker. The native
 * `<input type="file">` is sr-only and proxied via a ref so a single
 * input handles both click and drop pipelines through the same
 * `onSelect` callback supplied by the parent.
 *
 * Header row (label + required/optional badge + description) sits on top
 * of the body and stays visible across all states so the user can always
 * tell which slot they are looking at.
 */

export interface DocumentSlotConfig {
  type: UploadDocumentType;
  label: string;
  description: string;
  required: boolean;
}

export interface DocumentSlotState {
  status: 'idle' | 'uploading' | 'uploaded' | 'error';
  filename?: string;
  sizeBytes?: number;
  documentId?: string;
  errorMessage?: string;
}

export interface DocumentDropCardProps {
  config: DocumentSlotConfig;
  slot: DocumentSlotState;
  /** Disable while another slot is uploading (single-flight UX). */
  disabled: boolean;
  /** Accepted mime types — forwarded to the picker `accept` attribute. */
  acceptedMimeTypes: ReadonlyArray<UploadMimeType>;
  /** Soft cap shown in the format hint (BE also enforces). */
  maxSizeBytes: number;
  /**
   * Called when the user picks (or drops) a file. Validation lives in
   * the parent so the click + drop paths share the same pipeline.
   */
  onSelect: (file: File) => void;
  /** Reset to idle — used by the trash icon on uploaded / error states. */
  onReset: () => void;
}

export function DocumentDropCard({
  config,
  slot,
  disabled,
  acceptedMimeTypes,
  maxSizeBytes,
  onSelect,
  onReset,
}: DocumentDropCardProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [isOver, setIsOver] = useState(false);

  const acceptAttr = acceptedMimeTypes.join(',');
  const maxSizeMb = Math.round(maxSizeBytes / (1024 * 1024));
  const isUploading = slot.status === 'uploading';
  const dropDisabled = disabled || isUploading;

  const openPicker = useCallback(() => {
    if (dropDisabled) return;
    inputRef.current?.click();
  }, [dropDisabled]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset so re-picking the same file fires onChange again.
      e.target.value = '';
      if (file) onSelect(file);
    },
    [onSelect],
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLLIElement>) => {
      if (dropDisabled) return;
      if (!Array.from(e.dataTransfer.types).includes('Files')) return;
      e.preventDefault();
      dragDepth.current += 1;
      setIsOver(true);
    },
    [dropDisabled],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLLIElement>) => {
      if (dropDisabled) return;
      if (!Array.from(e.dataTransfer.types).includes('Files')) return;
      // dragover MUST preventDefault for the browser to allow the drop.
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    [dropDisabled],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLLIElement>) => {
      if (dropDisabled) return;
      e.preventDefault();
      // dragenter/leave fire on every nested element; counter avoids flicker.
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setIsOver(false);
    },
    [dropDisabled],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLLIElement>) => {
      if (dropDisabled) return;
      e.preventDefault();
      dragDepth.current = 0;
      setIsOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) onSelect(file);
    },
    [dropDisabled, onSelect],
  );

  return (
    <li
      className={cn(
        'rounded-xl border border-app-border bg-app-surface p-4 text-app-text shadow-md backdrop-blur-xl transition-colors',
        isOver && 'border-foreground/40 bg-foreground/5 ring-2 ring-inset ring-foreground/20',
      )}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header: label + badge */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">
              {config.label}
            </span>
            <SoftBadge tone={config.required ? 'info' : 'neutral'}>
              {config.required ? 'Obbligatorio' : 'Opzionale'}
            </SoftBadge>
          </div>
          <p className="text-xs text-muted-foreground">{config.description}</p>
        </div>
        {slot.status === 'uploaded' ? (
          <SoftBadge tone="success">
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
            Caricato
          </SoftBadge>
        ) : null}
      </div>

      {/* Body */}
      <div className="mt-3">
        {slot.status === 'idle' || slot.status === 'error' ? (
          <DropZoneTrigger
            onClick={openPicker}
            disabled={dropDisabled}
            acceptHint={`PDF, JPEG, PNG · max ${maxSizeMb} MB`}
            isError={slot.status === 'error'}
          />
        ) : null}

        {slot.status === 'uploading' ? (
          <FileRow
            icon={<Loader2 className="h-5 w-5 animate-spin" aria-hidden />}
            filename={slot.filename ?? 'caricamento…'}
            sizeBytes={slot.sizeBytes}
            statusText="Caricamento…"
            tone="neutral"
          />
        ) : null}

        {slot.status === 'uploaded' ? (
          <FileRow
            icon={<FileText className="h-5 w-5 text-foreground" aria-hidden />}
            filename={slot.filename ?? ''}
            sizeBytes={slot.sizeBytes}
            tone="neutral"
            actions={
              <>
                <PillButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={openPicker}
                  disabled={dropDisabled}
                  aria-label="Sostituisci file"
                >
                  <RefreshCcw className="h-3.5 w-3.5" aria-hidden />
                  Sostituisci
                </PillButton>
                {/* eslint-disable-next-line fe-gov/no-raw-html-primitives -- intentional: icon-only delete button (no Trash variant on PillButton; rounded-full pill would clash with the inline file row) */}
                <button
                  type="button"
                  onClick={onReset}
                  disabled={dropDisabled}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-state-danger/10 hover:text-state-danger disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Rimuovi file"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              </>
            }
          />
        ) : null}
      </div>

      {slot.status === 'error' && slot.errorMessage ? (
        <FieldError className="mt-2" message={slot.errorMessage} />
      ) : null}

      {/* eslint-disable-next-line fe-gov/no-raw-html-primitives -- intentional: sr-only file input proxied via ref; the visible affordance is the DropZoneTrigger / Sostituisci button */}
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        accept={acceptAttr}
        onChange={handleInputChange}
        disabled={dropDisabled}
        aria-hidden
        tabIndex={-1}
      />
    </li>
  );
}

// ── Body presentational pieces ──────────────────────────────────────────────

interface DropZoneTriggerProps {
  onClick: () => void;
  disabled: boolean;
  acceptHint: string;
  isError: boolean;
}

function DropZoneTrigger({
  onClick,
  disabled,
  acceptHint,
  isError,
}: DropZoneTriggerProps): JSX.Element {
  return (
    /* eslint-disable-next-line fe-gov/no-raw-html-primitives -- intentional: bespoke drop-zone CTA — dashed border + plus avatar + 2-line stack don't fit Button/PillButton (round pill) and the whole card is also a drop target */
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group flex w-full items-center gap-3 rounded-lg border border-dashed border-app-border bg-foreground/[0.02] px-4 py-3 text-left transition-colors',
        'hover:border-foreground/30 hover:bg-foreground/[0.04]',
        'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foreground/30',
        'disabled:cursor-not-allowed disabled:opacity-50',
        isError && 'border-state-danger/40 bg-state-danger/5',
      )}
    >
      <span
        className={cn(
          'flex h-9 w-9 flex-none items-center justify-center rounded-full',
          isError
            ? 'bg-state-danger/15 text-state-danger'
            : 'bg-foreground/10 text-foreground',
        )}
        aria-hidden
      >
        {isError ? (
          <AlertCircle className="h-4 w-4" />
        ) : (
          <Plus className="h-4 w-4" />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span
          className={cn(
            'text-sm font-medium',
            isError ? 'text-state-danger' : 'text-foreground',
          )}
        >
          {isError ? 'Riprova il caricamento' : 'Aggiungi documento'}
        </span>
        <span className="text-xs text-muted-foreground">
          Trascina il file qui o clicca per caricare · {acceptHint}
        </span>
      </span>
    </button>
  );
}

interface FileRowProps {
  icon: React.ReactNode;
  filename: string;
  sizeBytes?: number;
  statusText?: string;
  tone: 'neutral' | 'danger';
  actions?: React.ReactNode;
}

function FileRow({
  icon,
  filename,
  sizeBytes,
  statusText,
  tone,
  actions,
}: FileRowProps): JSX.Element {
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border bg-foreground/[0.02] px-3 py-2.5',
        tone === 'danger'
          ? 'border-state-danger/30'
          : 'border-app-border',
      )}
    >
      <span
        className={cn(
          'flex h-9 w-9 flex-none items-center justify-center rounded-md',
          tone === 'danger'
            ? 'bg-state-danger/15 text-state-danger'
            : 'bg-foreground/5 text-foreground',
        )}
      >
        {icon}
      </span>
      <div className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="truncate text-sm font-medium text-foreground">
          {filename}
        </span>
        <span className="text-xs text-muted-foreground">
          {sizeBytes !== undefined ? formatSize(sizeBytes) : null}
          {sizeBytes !== undefined && statusText ? ' · ' : null}
          {statusText ?? null}
        </span>
      </div>
      {actions ? (
        <div className="flex flex-none items-center gap-1.5">{actions}</div>
      ) : null}
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
